import { isRetired } from './learning.js';
const MATURE_STABILITY_THRESHOLD_DAYS = 21;
const DAY_MS = 24 * 60 * 60 * 1000;
function toDateStr(iso) {
    return iso.slice(0, 10);
}
function addDays(base, days) {
    return new Date(base.getTime() + days * DAY_MS);
}
/**
 * Computes dashboard/stats metrics from a user's cards and review history.
 * Pure function: no storage or network access, safe to unit test directly.
 *
 * Cards the learner retired ("I already know this word") are left out of every card number, and
 * so are chosen words whose text is still being prepared. A card that has not been started yet
 * (waiting in "Up next to learn") counts as a card and as "New", but is never "due".
 */
export function computeDashboardStats(allCards, logs, now = new Date()) {
    const cards = allCards.filter((c) => !isRetired(c) && c.content_status !== 'pending');
    const totalReviews = logs.length;
    const successfulReviews = logs.filter((l) => l.rating >= 2).length;
    const retentionRate = totalReviews > 0 ? successfulReviews / totalReviews : 0;
    const reviewDays = new Set(logs.map((l) => toDateStr(l.reviewed_at)));
    // Current streak: consecutive days with >=1 review, counting back from today
    // (or from yesterday if today has no review logged yet).
    let currentStreakDays = 0;
    {
        let cursor = new Date(now);
        if (!reviewDays.has(toDateStr(cursor.toISOString()))) {
            cursor = addDays(cursor, -1);
        }
        while (reviewDays.has(toDateStr(cursor.toISOString()))) {
            currentStreakDays++;
            cursor = addDays(cursor, -1);
        }
    }
    // Longest streak: longest run of consecutive calendar days across all history
    let longestStreakDays = 0;
    {
        const sortedDays = Array.from(reviewDays).sort();
        let run = 0;
        let prevTime = null;
        for (const d of sortedDays) {
            const t = new Date(`${d}T00:00:00Z`).getTime();
            run = prevTime !== null && t - prevTime === DAY_MS ? run + 1 : 1;
            longestStreakDays = Math.max(longestStreakDays, run);
            prevTime = t;
        }
    }
    const reviewsLast14Days = [];
    for (let i = 13; i >= 0; i--) {
        const dateStr = toDateStr(addDays(now, -i).toISOString());
        const count = logs.filter((l) => toDateStr(l.reviewed_at) === dateStr).length;
        reviewsLast14Days.push({ date: dateStr, count });
    }
    const dueNext7Days = [];
    for (let i = 0; i < 7; i++) {
        const dateStr = toDateStr(addDays(now, i).toISOString());
        const count = cards.filter((c) => c.fsrs_state !== 'New' && toDateStr(c.due_date) === dateStr).length;
        dueNext7Days.push({ date: dateStr, count });
    }
    const stateBreakdown = {
        New: 0,
        Learning: 0,
        Review: 0,
        Relearning: 0,
    };
    for (const c of cards) {
        stateBreakdown[c.fsrs_state] = (stateBreakdown[c.fsrs_state] || 0) + 1;
    }
    const matureCards = cards.filter((c) => c.stability >= MATURE_STABILITY_THRESHOLD_DAYS).length;
    return {
        totalCards: cards.length,
        matureCards,
        totalReviews,
        retentionRate,
        currentStreakDays,
        longestStreakDays,
        reviewsLast14Days,
        dueNext7Days,
        stateBreakdown,
    };
}
